'use client'

import { useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { MessageKeys, useTranslations } from 'next-intl'
import { ProductFacet } from '@/app/api/types'
import { Button } from '@/components/ui/button'
import { X, Filter } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose
} from '@/components/ui/sheet'

interface ProductFiltersProps {
  facetCounts: Array<ProductFacet>;
  className?: string;
}

interface FilterContentProps {
  facetCounts: Array<ProductFacet>;
  facetSearches: Record<string, string>;
  handleFacetSearch: (fieldName: string, value: string) => void;
  isSelected: (fieldName: string, value: string) => boolean;
  handleCheckboxChange: (fieldName: string, value: string, checked: boolean) => void;
  formatFieldName: (fieldName: string) => string;
  getFilteredFacetValues: (facet: ProductFacet, searchTerm: string) => typeof facet.counts;
}

// FilterContent Component - Extracted outside of the main component
function FilterContent({
  facetCounts,
  facetSearches,
  handleFacetSearch,
  isSelected,
  handleCheckboxChange,
  formatFieldName,
  getFilteredFacetValues
}: FilterContentProps) {
  const t = useTranslations('Store.Products.Search.Filters')

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold mb-6">{t('title')}</h2>

      {facetCounts.filter(facet => facet.field_name !== 'category').map(facet => {
        const fieldName = facet.field_name
        const displayName = formatFieldName(fieldName)
        const searchTerm = facetSearches[fieldName] || ''
        const filteredValues = getFilteredFacetValues(facet, searchTerm)
        const totalValues = facet.stats.total_values

        return (
          <div key={fieldName} className="space-y-3">
            <h3 className="text-lg font-semibold">{displayName}</h3>

            {totalValues > filteredValues.length && (
              <Input
                type="text"
                placeholder={`${t('Enter')} ${displayName.toLowerCase()}`}
                value={searchTerm}
                onChange={(e) => handleFacetSearch(fieldName, e.target.value)}
                className="w-full mb-2"
              />
            )}

            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {filteredValues.length > 0 ? (
                filteredValues.map(item => (
                  <div key={`${fieldName}-${item.value}`} className="flex items-center gap-2">
                    <Checkbox
                      id={`${fieldName}-${item.value}`}
                      checked={isSelected(fieldName, item.value)}
                      onCheckedChange={(checked) =>
                        handleCheckboxChange(fieldName, item.value, checked === true)
                      }
                    />
                    <Label
                      htmlFor={`${fieldName}-${item.value}`}
                      className="flex-1 cursor-pointer text-sm flex justify-between"
                    >
                      <span>{t.has(item.value as MessageKeys<typeof t, keyof typeof t>) ? t(item.value as MessageKeys<typeof t, keyof typeof t>) : item.value}</span>
                      <span className="text-gray-500">({item.count})</span>
                    </Label>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No options found</p>
              )}

              {totalValues > filteredValues.length && (
                <p className="text-sm text-gray-500">
                  {t('moreFacets', { count: totalValues - filteredValues.length })}
                </p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function ProductFilters({ facetCounts, className }: ProductFiltersProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const t = useTranslations('Store.Products.Search.Filters')
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [facetSearches, setFacetSearches] = useState<Record<string, string>>({})

  // Format field name for display (capitalize and remove camelCase)
  const formatFieldName = (fieldName: string): string => {
    if (t.has(fieldName as MessageKeys<typeof t, keyof typeof t>)) {
      return t(fieldName as MessageKeys<typeof t, keyof typeof t>)
    }

    return fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
  }

  // Filter facet values based on search input
  const getFilteredFacetValues = (facet: typeof facetCounts[0], searchTerm: string) => {
    if (!searchTerm) return facet.counts

    return facet.counts.filter(count =>
      count.value.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }

  // Parse the current filter_by parameter to extract all field values
  const parseFilterBy = useCallback((filterByParam: string | null): Record<string, string[]> => {
    const result: Record<string, string[]> = {}

    if (!filterByParam) return result

    // Split by && to get individual field conditions
    const conditions = filterByParam.split('&&').map(c => c.trim())

    conditions.forEach(condition => {
      // Check for array notation [value1, value2]
      const arrayMatch = condition.match(/([^:\s]+)\s*:\s*\[(.*?)\]/)
      if (arrayMatch) {
        const fieldName = arrayMatch[1].trim()
        // Split array values and clean them
        const values = arrayMatch[2]
          .split(',')
          .map(v => v.trim().replace(/^['"`]|['"`]$/g, ''))

        result[fieldName] = values
      } else {
        // Handle single value condition
        const parts = condition.split(':')
        if (parts.length >= 2) {
          const fieldName = parts[0].trim()
          const value = parts.slice(1).join(':').trim()

          if (!result[fieldName]) {
            result[fieldName] = []
          }
          result[fieldName].push(value)
        }
      }
    })

    return result
  }, [])

  const appendToFilterBy = useCallback((fieldName: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    const currentFilterBy = params.get('filter_by') || ''
    const selectionValue = `${fieldName}: ${value}`

    if (currentFilterBy) {
      params.set('filter_by', `${currentFilterBy} && ${selectionValue}`)
    } else {
      params.set('filter_by', selectionValue)
    }

    return params
  }, [searchParams])

  const removeFromFilterBy = useCallback((fieldName: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    const currentFilterBy = params.get('filter_by') || ''
    const selectionValue = `${fieldName}: ${value}`

    if (currentFilterBy) {
      // Remove the selection value and handle potential && artifacts
      let newFilterBy = currentFilterBy
        .replace(`${selectionValue} && `, '') // Remove if it's at the start
        .replace(` && ${selectionValue}`, '') // Remove if it's at the end
        .replace(selectionValue, '') // Remove if it's the only filter
        .trim()

      // Clean up any potential double &&s that might be left
      newFilterBy = newFilterBy.replace(/\s*&&\s*&&\s*/g, ' && ').trim()

      if (newFilterBy && newFilterBy !== '&&') {
        params.set('filter_by', newFilterBy)
      } else {
        params.delete('filter_by')
      }
    }

    return params
  }, [searchParams])

  // Directly update URL when a checkbox is clicked instead of rebuilding entire filter state
  const handleCheckboxChange = useCallback((fieldName: string, value: string, checked: boolean) => {
    let newParams
    // Add or remove the specific value
    if (checked) {
      // Add the value
      newParams = appendToFilterBy(fieldName, value)
    } else {
      // Remove the value
      newParams = removeFromFilterBy(fieldName, value)
    }
    newParams.delete('page')

    // Close the drawer when applying a filter on mobile
    if (window.innerWidth < 768) {
      setIsDrawerOpen(false)
    }

    router.push(`?${newParams.toString()}#products`)
  }, [router, appendToFilterBy, removeFromFilterBy])

  // Check if a facet value is currently selected
  const isSelected = useCallback((fieldName: string, value: string): boolean => {
    const currentFilterBy = searchParams.get('filter_by') || ''
    const filterState = parseFilterBy(currentFilterBy)

    return !!filterState[fieldName]?.includes(value)
  }, [searchParams, parseFilterBy])

  // Update facet search term
  const handleFacetSearch = (fieldName: string, value: string) => {
    setFacetSearches(prev => ({
      ...prev,
      [fieldName]: value
    }))
  }

  return (
    <>
      {/* Mobile filter button */}
      <div className="md:hidden mb-4">
        <Button
          variant="outline"
          className="w-full flex items-center justify-center gap-2"
          onClick={() => setIsDrawerOpen(true)}
        >
          <Filter size={16} />
          <span>{t('title')}</span>
        </Button>
      </div>

      {/* Mobile filters drawer */}
      <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <SheetContent side="left" className="w-[85vw] max-w-md overflow-y-auto">
          <SheetTitle className="sr-only">{t('title')}</SheetTitle>
          <FilterContent
            facetCounts={facetCounts}
            facetSearches={facetSearches}
            handleFacetSearch={handleFacetSearch}
            isSelected={isSelected}
            handleCheckboxChange={handleCheckboxChange}
            formatFieldName={formatFieldName}
            getFilteredFacetValues={getFilteredFacetValues}
          />
        </SheetContent>
      </Sheet>

      {/* Desktop filters (unchanged) */}
      <div className={cn("space-y-8 hidden md:block", className)}>
        <FilterContent
          facetCounts={facetCounts}
          facetSearches={facetSearches}
          handleFacetSearch={handleFacetSearch}
          isSelected={isSelected}
          handleCheckboxChange={handleCheckboxChange}
          formatFieldName={formatFieldName}
          getFilteredFacetValues={getFilteredFacetValues}
        />
      </div>
    </>
  )
} 